import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import DataTable from "../../../../../Common/DataTable/DataTable";
import {
  GET_ALL_RESERVATION_LIST_REQUEST,
  PATCH_ROOM_BOOKING_REQUEST,
  PUT_ROOM_REASSIGN_REQUEST,
} from "../../../../../Utils/Constant";
import { roomTypesColumns } from "../RoomTypeColumns";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";

import {
  Eye,
  EllipsisVertical,
  LogOut,
  UserCheck,
  CheckLine,
  Search,
  Filter,
  X,
} from "lucide-react";
// import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RoomReassignmentModal from "../../../../../Common/RoomReassignModel";
import { hasEditAccess } from "../../../../../Common/CheckAccess";

const RoomBookingTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useParams();

  const [bookingData, setBookingData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  
  // Filter and search states
  const [filters, setFilters] = useState({
    search: "",
    booking_status: "",
    check_in_start_date: "",
    check_in_end_date: "",
  });
  const [filterType, setFilterType] = useState("Guest Name");
  const [globalFilter, setGlobalFilter] = useState("");
  
  const filterOptions = ["Guest Name", "Booking Status", "Room Number", "Date Range"];
  const bookingStatusOptions = ["booked", "check_in", "check_out", "cancelled"];

  const { allReservation, loading, reassign, addReservation, updateBooking } =
    useSelector((state) => state.roomReservationList);

  const [permissions, setPermissions] = useState([]);

  const existingPermissions = localStorage.getItem("checkPermission");
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);

  useEffect(() => {
    if (addReservation?.success) {
      dispatch({
        type: GET_ALL_RESERVATION_LIST_REQUEST,
        payload: { page, limit: pageSize, sortBy, sortOrder },
      });
    }
  }, [addReservation]);

  // Reset filters when filter type changes
  useEffect(() => {
    setGlobalFilter("");
    setFilters({
      search: "",
      booking_status: "",
      check_in_start_date: "",
      check_in_end_date: "",
    });
    setPage(1);
  }, [filterType]);

  useEffect(() => {
    const payload = { 
      page, 
      limit: pageSize, 
      sortBy, 
      sortOrder,
    };

    // Add filter parameters based on filter type and global filter
    if (globalFilter && globalFilter.trim()) {
      if (filterType === "Guest Name") {
        payload.guest_name = globalFilter.trim();
      } else if (filterType === "Booking Status") {
        payload.booking_status = globalFilter.trim();
        console.log('Booking Status Filter:', globalFilter.trim()); // Debug log
      } else if (filterType === "Room Number") {
        payload.room_number = globalFilter.trim();
      }
    }

    // Date range filters
    if (filters.check_in_start_date) {
      payload.check_in_start_date = filters.check_in_start_date;
    }

    if (filters.check_in_end_date) {
      payload.check_in_end_date = filters.check_in_end_date;
    }
    
    console.log('API Payload:', payload); // Debug log
    
    dispatch({
      type: GET_ALL_RESERVATION_LIST_REQUEST,
      payload,
    });
  }, [dispatch, page, pageSize, sortBy, sortOrder, reassign, updateBooking, globalFilter, filterType, filters.check_in_start_date, filters.check_in_end_date]);

  const handleReservatrionByID = (reservation_id) => {
    dispatch({
      type: GET_ALL_RESERVATION_LIST_REQUEST,
      payload: { reservation_id },
    });
    // navigate(/reservation/add-room-booking/${reservation_id}?isViewOnly=true);
    navigate(`/reservation/add-room-booking/${reservation_id}?isViewOnly=true`);

    setOpenMenuId(null);
  };

  const handleCheckout = (reservation_id) => {
    // Add your checkout logic here
    // navigate(/reservation/check-out-booking/${reservation_id}?isViewOnly=true);
    navigate(
      `/reservation/check-out-booking/${reservation_id}?isViewOnly=true`
    );

    // navigate(/reservation/checkout/${reservation_id});
    setOpenMenuId(null);
  };

  const handleReassign = (reservation_id) => {
    const reservation = allReservation?.data?.reservation?.find(
      (res) => res.id === reservation_id
    );
    if (reservation) {
      const guestInfo = {
        id: reservation.id,
        name: `${reservation.guest?.first_name ?? ""} ${
          reservation.guest?.last_name ?? ""
        }`.trim(),
        email: reservation.guest?.email ?? "-",
        rooms: reservation.rooms,
        // room_number: reservation.room_number,
        checkIn: reservation.check_in_date_time?.split("T")[0] ?? "-",
        checkOut: reservation.check_out_date_time?.split("T")[0] ?? "-",
      };

      setSelectedReservation(guestInfo);
      setShowReassignModal(true);
      setOpenMenuId(null);
    }
  };

  const handleConfirmReassignment = (
    reservationId,
    newRoomId,
    reason,
    notes
  ) => {
    // Dispatch an action here if needed
    dispatch({
      type: PUT_ROOM_REASSIGN_REQUEST,
      payload: {
        id: reservationId,
        data: { room_ids: newRoomId, change_reason: reason, note: notes },
      },
    });
    setShowReassignModal(false);
    if (location?.search === "?reassign=true" && location?.state?.id) {
      navigate("/front-desk/dashboard?from=reservation");
    }
    setSelectedReservation(null);
    dispatch({
      type: GET_ALL_RESERVATION_LIST_REQUEST,
      payload: { page, limit: pageSize, sortBy, sortOrder },
    });
  };

  const handleCheckIn = (reservation_id) => {
    dispatch({
      type: PATCH_ROOM_BOOKING_REQUEST,
      payload: {
        reservation_id,
        body: { booking_status: "check_in", type: "checkin" },
      },
    });
  };

  const ActionDropdown = ({ reservationStatus, reservation_id }) => {
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      left: 0,
    });
    const [isValidPosition, setIsValidPosition] = useState(false);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const calculatePosition = () => {
      if (!buttonRef.current) {
        setIsValidPosition(false);
        return false;
      }

      try {
        const rect = buttonRef.current.getBoundingClientRect();

        // Validate that we have a proper bounding rect
        if (!rect || rect.width === 0 || rect.height === 0) {
          setIsValidPosition(false);
          return false;
        }

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = 150;
        const dropdownHeight = 120;

        let top = rect.bottom + window.scrollY + 2;
        let left = rect.right + window.scrollX - dropdownWidth;

        // Boundary checks
        if (left < 0) {
          left = rect.left + window.scrollX;
        }
        if (left + dropdownWidth > viewportWidth) {
          left = viewportWidth - dropdownWidth - 10;
        }
        if (top + dropdownHeight > viewportHeight + window.scrollY) {
          top = rect.top + window.scrollY - dropdownHeight - 2;
        }

        // Final validation - ensure we don't have invalid positions
        if (top < 0 || left < 0 || isNaN(top) || isNaN(left)) {
          setIsValidPosition(false);
          return false;
        }

        setDropdownPosition({ top, left });
        setIsValidPosition(true);
        return true;
      } catch (error) {
        console.warn("Position calculation failed:", error);
        setIsValidPosition(false);
        return false;
      }
    };

    const handleMouseEnter = (e) => {
      e.stopPropagation();
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Only show dropdown if we can calculate a valid position
      if (calculatePosition()) {
        setOpenMenuId(reservation_id);
      }
    };

    const handleMouseLeave = () => {
      hoverTimeoutRef.current = setTimeout(() => {
        setOpenMenuId(null);
        setIsValidPosition(false);
      }, 150);
    };

    const handleDropdownMouseEnter = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };

    const handleDropdownMouseLeave = () => {
      setOpenMenuId(null);
      setIsValidPosition(false);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    // Close dropdown if this component unmounts while open
    useEffect(() => {
      return () => {
        if (openMenuId === reservation_id) {
          setOpenMenuId(null);
        }
      };
    }, []);

    return (
      <div className="position-relative">
        <button
          ref={buttonRef}
          className="btn btn-sm"
          title="More actions"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <EllipsisVertical size={16} />
        </button>

        {openMenuId === reservation_id && isValidPosition && (
          <div
            ref={dropdownRef}
            className="position-fixed bg-white border rounded shadow-lg"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 2000,
              minWidth: "150px",
              maxWidth: "200px",
            }}
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            <div className="py-1">
              <button
                className="w-100 text-start border-0 bg-transparent px-3 py-2 d-flex align-items-center hover-bg-light"
                onClick={() => handleReservatrionByID(reservation_id)}
                style={{ fontSize: "14px" }}
              >
                <Eye size={14} className="me-2" />
                View
              </button>
              {reservationStatus === "booked" && (
                <button
                  className="w-100 text-start border-0 bg-transparent px-3 py-2 d-flex align-items-center"
                  onClick={() => handleCheckIn(reservation_id)}
                  style={{ fontSize: "14px" }}
                >
                  <CheckLine size={14} className="me-2" />
                  Check In
                </button>
              )}
              {reservationStatus !== "check_out" &&
                reservationStatus !== "cancelled" && (
                  <>
                    <button
                      className="w-100 text-start border-0 bg-transparent px-3 py-2 d-flex align-items-center"
                      onClick={() => handleCheckout(reservation_id)}
                      style={{ fontSize: "14px" }}
                    >
                      <LogOut size={14} className="me-2" />
                      Checkout
                    </button>
                    {hasEditAccess(permissions, "reservation") && (
                      <button
                        className="w-100 text-start border-0 bg-transparent px-3 py-2 d-flex align-items-center"
                        onClick={() => handleReassign(reservation_id)}
                        style={{ fontSize: "14px" }}
                      >
                        <UserCheck size={14} className="me-2" />
                        Reassign
                      </button>
                    )}
                  </>
                )}
              {["admin", "super_admin"]?.includes(role) && (
                <button
                  className="w-100 text-start border-0 bg-transparent px-3 py-2 d-flex align-items-center"
                  onClick={() => {
                    navigate("/reservation/booking-logs", {
                      state: { reservation_id },
                    });
                  }}
                  style={{ fontSize: "14px" }}
                >
                  <UserCheck size={14} className="me-2" />
                  View Logs
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (allReservation?.data?.reservation?.length) {
      const extractedData = allReservation.data.reservation.map(
        (reservation) => ({
          first_name: reservation.guest?.last_name
            ? `${reservation.guest.first_name} ${reservation.guest.last_name}`
            : reservation.guest?.first_name || "-",

          room_type: reservation.rooms?.length
            ? reservation.rooms?.map((item) => item.room?.room_type)?.join(", ")
            : "-",
          room_number: reservation.rooms?.length
            ? reservation.rooms?.map((item) => item.room_number)?.join(", ")
            : "-",
          check_in: reservation.check_in_date_time.includes(" ")
            ? reservation.check_in_date_time?.split(" ")[0]
            : reservation.check_in_date_time?.split("T")[0] || "-",
          check_out: reservation.check_out_date_time.includes(" ")
            ? reservation.check_out_date_time?.split(" ")[0]
            : reservation.check_out_date_time?.split("T")[0] || "-",
          total_amount: reservation?.total_amount || 0,
          status: (
            <span className="text-lowercase">
              {reservation.booking_status || "-"}
            </span>
          ),
          reservationList:
            location.pathname === "/reservation/booking-list" ? (
              <ActionDropdown
                reservationStatus={reservation?.booking_status}
                reservation_id={reservation.id}
              />
            ) : (
              <button
                className="btn btn-sm"
                title="View"
                onClick={() => handleReservatrionByID(reservation.id)}
              >
                <Eye size={16} />
              </button>
            ),
        })
      );

      setBookingData(extractedData);
    } else {
      setBookingData([]);
    }
  }, [allReservation, openMenuId]);

  // Track last reassign id to avoid repeated calls
  const lastReassignIdRef = useRef(null);

  useEffect(() => {
    const reassignId =
      location?.search === "?reassign=true" && location?.state?.id;
    if (
      reassignId &&
      bookingData?.length &&
      lastReassignIdRef.current !== reassignId
    ) {
      handleReassign(reassignId);
      lastReassignIdRef.current = reassignId;
    }
  }, [location, bookingData]);

  const handleSortChange = (sort) => {
    setSortBy(sort.id);
    setSortOrder(sort.desc ? "desc" : "asc");
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      booking_status: "",
      check_in_start_date: "",
      check_in_end_date: "",
    });
    setGlobalFilter("");
    setPage(1);
  };

  const hasActiveFilters = globalFilter || filters.check_in_start_date || filters.check_in_end_date;

  // Extract total entries and total pages from API pagination info
  const totalEntries = allReservation?.data?.pagination?.totalResults || 0;
  const totalPages = allReservation?.data?.pagination?.totalPages || 1;

  return (
    <div>
            {/* Filter Section - Matching RoomList Pattern */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-wrap justify-content-end align-items-center gap-3">
            {/* Filter Type Dropdown */}
            {filterType === "Date Range" ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label className="form-label mb-0">Select Filter:</label>
                <select
                  className="form-select"
                  style={{ width: "200px" }}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {filterOptions.map((option, index) => (
                    <option value={option} key={index}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <select
                  className="form-select"
                  style={{ width: "200px" }}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {filterOptions.map((option, index) => (
                    <option value={option} key={index}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Conditional Input Fields */}
            {filterType === "Date Range" ? (
              <>
                <div>
                  <label className="form-label mb-0">Start Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.check_in_start_date || ""}
                    onChange={(e) => handleFilterChange("check_in_start_date", e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label mb-0">End Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.check_in_end_date || ""}
                    onChange={(e) => handleFilterChange("check_in_end_date", e.target.value)}
                    min={filters.check_in_start_date || ""}
                  />
                </div>
              </>
            ) : filterType === "Booking Status" ? (
              <>
                <label className="form-label mb-0">Status:</label>
                <select
                  className="form-select"
                  style={{ width: "200px" }}
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                >
                  <option value="">Select Status</option>
                  {bookingStatusOptions.map((status, index) => (
                    <option value={status} key={index}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="form-label mb-0">Search:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Enter ${filterType.toLowerCase()}`}
                  style={{ width: "200px" }}
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
              </>
            )}

            {/* Clear Button */}
            {hasActiveFilters && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleClearFilters}
              >
                <X size={14} className="me-1" />
                Clear
              </button>
            )}
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mt-3">
              <div className="d-flex flex-wrap gap-2">
                <small className="text-muted me-2">Active Filters:</small>
                {globalFilter && (
                  <span className="badge bg-primary px-3 py-2">
                    {filterType}: {globalFilter}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      style={{ fontSize: '8px' }}
                      onClick={() => setGlobalFilter("")}
                    ></button>
                  </span>
                )}
                {(filters.check_in_start_date || filters.check_in_end_date) && (
                  <span className="badge bg-warning text-dark px-3 py-2">
                    Date: {filters.check_in_start_date || 'Any'} - {filters.check_in_end_date || 'Any'}
                    <button
                      type="button"
                      className="btn-close ms-2"
                      style={{ fontSize: '8px' }}
                      onClick={() => {
                        handleFilterChange("check_in_start_date", "");
                        handleFilterChange("check_in_end_date", "");
                      }}
                    ></button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* UnComment this if you want to show total  */}
      {/* <div className="row mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm border-start border-primary border-4">
            <div className="card-body">
              <h6 className="text-muted">Total Reservations</h6>
              <h5 className="fw-bold">
                {allReservation?.data?.pagination?.totalResults ?? 0}
              </h5>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm border-start border-success border-4">
            <div className="card-body">
              <h6 className="text-muted">Total Revenue</h6>
              <h5 className="fw-bold">
                ₹
                {allReservation?.data?.reservation
                  ?.reduce(
                    (sum, item) => sum + parseFloat(item?.total_amount ?? 0),
                    0
                  )
                  .toLocaleString("en-IN")}
              </h5>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm border-start border-warning border-4">
            <div className="card-body">
              <h6 className="text-muted">Checked-out</h6>
              <h5 className="fw-bold">
                {
                  allReservation?.data?.reservation?.filter(
                    (item) => item.booking_status === "check_out"
                  ).length
                }
              </h5>
            </div>
          </div>
        </div>
      </div> */}

      <DataTable
        columns={roomTypesColumns}
        data={bookingData}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onSortChange={handleSortChange}
        isLoading={loading}
      />
      <DataTableFooter
        currentPage={page}
        totalPages={totalPages}
        setCurrentPage={handlePageChange}
        totalEntries={totalEntries}
        pageSize={pageSize}
      />
      {showReassignModal && selectedReservation && (
        <RoomReassignmentModal
          isOpen={showReassignModal}
          onClose={() => {
            setShowReassignModal(false);
            if (location?.search === "?reassign=true" && location?.state?.id) {
              navigate("/front-desk/dashboard?from=reservation");
            }
          }}
          guest={selectedReservation}
          onReassign={handleConfirmReassignment}
        />
      )}

      {/* {selectedReservation && (
        <RoomReassignmentModal
          show={showReassignModal}
          handleClose={() => setShowReassignModal(false)}
          reservation={selectedReservation}
          onConfirm={handleConfirmReassignment}
        />
      )} */}
    </div>
  );
};

export default RoomBookingTable;
